"use client";

import { useEffect, useRef, useState } from "react";
import type { Locale } from "@/lang";
import { StoryLottie, phoneStageClass } from "./StoryLottie";

type LiveStageProps = {
  /** Scene name (`public/lottie/<name>.<locale>.json`). */
  name: string;
  locale: Locale;
  /** Accessible description of what the animation shows. */
  label: string;
  /** Accessible name of the stage button ("Replay the … animation"). */
  replayLabel: string;
  /**
   * Stage shape and ground (size, ratio, radius, background, ring, shadow). Below `sm` a
   * scene with a phone cut gets that cut's ratio on top (see `phoneStageClass`).
   */
  className?: string;
  /** Download right away instead of when the stage nears the viewport (above the fold). */
  eager?: boolean;
};

/**
 * An animation stage: it downloads when near the viewport, plays once when mostly visible,
 * and replays from the start when clicked. The stage itself never moves; scenes that carry
 * an `idle` marker keep their own elements gently alive after playing (see StoryLottie).
 * Reduced motion shows the final picture.
 */
export function LiveStage({ name, locale, label, replayLabel, className = "", eager = false }: LiveStageProps) {
  const stageRef = useRef<HTMLButtonElement>(null);
  const [near, setNear] = useState(eager);
  const [inView, setInView] = useState(false);
  const [replay, setReplay] = useState(0);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const nearIo = new IntersectionObserver(([e]) => e.isIntersecting && setNear(true), { rootMargin: "60% 0px" });
    const viewIo = new IntersectionObserver(([e]) => setInView(e.intersectionRatio >= 0.5), { threshold: [0, 0.5] });
    nearIo.observe(el);
    viewIo.observe(el);
    return () => {
      nearIo.disconnect();
      viewIo.disconnect();
    };
  }, []);

  return (
    <button
      ref={stageRef}
      type="button"
      onClick={() => setReplay((n) => n + 1)}
      aria-label={replayLabel}
      className={`relative block w-full cursor-pointer transition duration-300 ease-in-out ${className} ${phoneStageClass(name)}`}
    >
      <StoryLottie
        name={name}
        locale={locale}
        load={near}
        active={inView}
        replay={replay}
        label={label}
        className="absolute inset-0"
      />
    </button>
  );
}
